import { Injectable, inject, NgZone } from '@angular/core';
import {
  HttpClient,
  HttpEventType,
  HttpResponse,
  HttpUploadProgressEvent,
} from '@angular/common/http';
import { Observable, map, filter } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { Resource } from '../grading.types';

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

export interface UploadProgress {
  type: 'progress';
  progress: number;
}

export interface UploadComplete {
  type: 'complete';
  resources: Resource[];
}

export type UploadEvent = UploadProgress | UploadComplete;

@Injectable({
  providedIn: 'root',
})
export class FilesApiService {
  private readonly http = inject(HttpClient);
  private readonly ngZone = inject(NgZone);
  private readonly gradingApiUrl = `${environment.apiUrl}/grading`;
  private readonly resourcesApiUrl = `${environment.apiUrl}/resources`;

  /**
   * Upload files to a grading session
   * Emits progress events and completes with uploaded resources
   */
  uploadFiles(gradingId: string, files: File[]): Observable<UploadEvent> {
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file);
    }

    return this.http
      .post<ApiResponse<{ resources: Resource[] }>>(
        `${this.gradingApiUrl}/${gradingId}/files`,
        formData,
        {
          reportProgress: true,
          observe: 'events',
        },
      )
      .pipe(
        filter(
          (
            event,
          ): event is
            | HttpUploadProgressEvent
            | HttpResponse<ApiResponse<{ resources: Resource[] }>> =>
            event.type === HttpEventType.UploadProgress ||
            event.type === HttpEventType.Response,
        ),
        map((event): UploadEvent => {
          if (event.type === HttpEventType.UploadProgress) {
            const progress = event.total
              ? Math.round((event.loaded / event.total) * 100)
              : 0;
            return { type: 'progress', progress };
          }
          // Response
          return {
            type: 'complete',
            resources: event.body?.data?.resources ?? [],
          };
        }),
      );
  }

  /**
   * Remove a pending file from the grading session
   */
  removeFile(gradingId: string, resourceId: string): Observable<void> {
    return this.http
      .delete<ApiResponse<void>>(
        `${this.gradingApiUrl}/${gradingId}/files/${resourceId}`,
      )
      .pipe(map(() => undefined));
  }

  /**
   * Watch resource processing status via SSE
   * Emits resource updates until processing is complete or failed
   *
   * Features automatic reconnection with exponential backoff on connection failures.
   *
   * @param resourceId - The resource ID to watch
   * @param maxRetries - Maximum number of reconnection attempts (default: 10)
   */
  watchResourceStatus(resourceId: string, maxRetries = 10): Observable<Resource> {
    return new Observable<Resource>((subscriber) => {
      const url = `${this.resourcesApiUrl}/${resourceId}/sse`;
      let eventSource: EventSource | null = null;
      let retryCount = 0;
      let retryTimeout: ReturnType<typeof setTimeout> | null = null;
      let isCompleted = false;
      let isUnsubscribed = false;

      const connect = () => {
        if (isUnsubscribed || isCompleted) {
          return;
        }

        eventSource = new EventSource(url);

        eventSource.onmessage = (event) => {
          // Reset retry count on successful message
          retryCount = 0;

          // Run inside Angular zone to trigger change detection
          this.ngZone.run(() => {
            try {
              const resource = JSON.parse(event.data) as Resource;
              subscriber.next(resource);

              // Complete when processing is done
              if (
                resource.processingStatus === 'completed' ||
                resource.processingStatus === 'failed'
              ) {
                isCompleted = true;
                eventSource?.close();
                subscriber.complete();
              }
            } catch (error) {
              isCompleted = true;
              subscriber.error(error);
              eventSource?.close();
            }
          });
        };

        eventSource.onerror = () => {
          this.ngZone.run(() => {
            eventSource?.close();

            // Don't retry if already completed or unsubscribed
            if (isCompleted || isUnsubscribed) {
              return;
            }

            // Check if we should retry
            if (retryCount < maxRetries) {
              retryCount++;
              // Exponential backoff: 1s, 2s (capped at 2s)
              const delay = Math.min(1000 * Math.pow(2, retryCount - 1), 2000);
              console.warn(
                `SSE connection failed, retrying in ${delay}ms (attempt ${retryCount}/${maxRetries})`,
              );
              retryTimeout = setTimeout(() => {
                connect();
              }, delay);
            } else {
              // Max retries exceeded
              console.error(
                `SSE connection failed after ${maxRetries} retry attempts`,
              );
              subscriber.error(
                new Error(`SSE connection failed after ${maxRetries} retry attempts`),
              );
            }
          });
        };
      };

      // Start initial connection
      connect();

      // Cleanup on unsubscribe
      return () => {
        isUnsubscribed = true;
        if (retryTimeout) {
          clearTimeout(retryTimeout);
        }
        eventSource?.close();
      };
    });
  }
}
